// Route guard for the /capture/* tree.
//
// The capture surface is the primary product: field staff and admins
// both have a legitimate reason to reach it. Field staff land here
// post-login to record counts; admins land here when they drill from
// the dashboard into a building to preview captured work (Admin
// Preview Mode, see docs/buildspec/capture/20-ui.md §1.1a — the
// read-only variant is wired in a later wave). Guarding at the tree
// root means there is exactly one place to audit for "who can see
// capture pages".
//
// Branching rules (order matters, mirrored from require-admin.tsx
// so the two guards stay behaviourally identical in everything except
// the role check):
//   1. 'loading' → neutral loading state, no navigation yet.
//   2. 'unauthenticated' → redirect out of the SPA to /auth/login so
//      the backend can kick off the Entra ID round-trip. We preserve
//      the current URL via `redirect_to` so the user lands back where
//      they were after sign-in.
//   3. 'error' → surface the error; do NOT redirect to login. A 5xx
//      on /me is not "sign in again", and silently routing to login
//      would mask genuine outages.
//   4. 'authenticated' → render children. Any authenticated role
//      (field_staff OR admin) passes — this is the core difference
//      from RequireAdmin.

import { useEffect, type ReactNode } from 'react'

import { buildLoginUrl, navigateTo } from '../hooks/api'
import { BrandedSplash } from '../ui/branded-splash'

import { useAuth } from './auth-provider'

export interface RequireAuthProps {
  children: ReactNode
}

export function RequireAuth({ children }: RequireAuthProps): ReactNode {
  const { status, error } = useAuth()

  useEffect(() => {
    if (status === 'unauthenticated' && typeof window !== 'undefined') {
      const here = `${window.location.pathname}${window.location.search}`
      navigateTo(buildLoginUrl(here))
    }
  }, [status])

  if (status === 'loading') {
    return <BrandedSplash message="Loading session…" />
  }

  if (status === 'unauthenticated') {
    // Effect above handles the redirect; render a neutral message in
    // the frame between render and navigation.
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center p-6 text-sm text-gray-600"
      >
        Redirecting to sign in…
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center p-6 text-sm text-red-700"
      >
        <div className="max-w-md space-y-2 text-center">
          <h1 className="text-lg font-semibold">Could not verify session</h1>
          <p>{error?.message ?? 'Unknown error contacting the server.'}</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
